import { useEffect, useState } from "react";
import { Button, Tab, Tabs, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { useNavigate, useParams } from "react-router-dom";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import { API } from "../../queries/api";
import { ClubType } from "../../types";
import { Avatar, Chip, IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChatIcon from "@mui/icons-material/Chat";
import GroupIcon from "@mui/icons-material/Group";
import LockIcon from "@mui/icons-material/Lock";
import useConfirmModal from "../../hooks/useConfirmModal";
import useSnackbar from "../../hooks/useSnackbar";
import SearchUser from "../user/SearchUser";
import useSelfUser from "../../hooks/useSelfUser";
import { matrixClient, MatrixRoomInfo } from "../../services/matrixClient";
import { loadClubPageChats } from "../../services/roomListing";

const COMMUNITY_PURPOSES = ["CLUB", "CLUB_CHANNEL", "CLUB_CHANEL_WITH_COMMENTS", "COALITION", "COALITION_CHANEL", "COALITION_CHANEL_WITH_COMMENTS"];
const PRIVATE_PURPOSES = ["USUAL", "PRIVATE"];

function getChatTypeChip(purpose?: string) {
	if (!purpose) return <Chip label="Unknown" size="small" sx={{ height: 20, fontSize: "0.7rem" }} />;
	if (COMMUNITY_PURPOSES.includes(purpose)) {
		return <Chip icon={<GroupIcon sx={{ fontSize: 14 }} />} label="Community" size="small" color="success" sx={{ height: 20, fontSize: "0.7rem" }} />;
	}
	if (PRIVATE_PURPOSES.includes(purpose)) {
		return <Chip icon={<LockIcon sx={{ fontSize: 14 }} />} label="Private" size="small" sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#e0e0e0" }} />;
	}
	if (purpose === "REQUEST_CONVERSATION") {
		return <Chip label="Request" size="small" color="info" sx={{ height: 20, fontSize: "0.7rem" }} />;
	}
	return <Chip label={purpose} size="small" sx={{ height: 20, fontSize: "0.7rem" }} />;
}

function ChatTable({ rooms, loading, onChatClick, unreadCounts, emptyText }: {
	rooms: MatrixRoomInfo[];
	loading: boolean;
	onChatClick: (id: string) => void;
	unreadCounts: Record<string, number>;
	emptyText: string;
}) {
	return (
		<TableContainer>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell>Room Name</TableCell>
						<TableCell>Type</TableCell>
						<TableCell>Members</TableCell>
						<TableCell>Description</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rooms.map((room) => (
						<TableRow
							key={room.id}
							sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" } }}
							onClick={() => onChatClick(room.id)}
						>
							<TableCell>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									{room.image ? (
										<Avatar
											src={matrixClient.getMediaUrl(room.image)}
											sx={{ width: 24, height: 24 }}
										/>
									) : (
										<ChatIcon sx={{ fontSize: 16, color: "#1976d2" }} />
									)}
									{room.name || room.id}
									{(unreadCounts[room.id] || 0) > 0 && (
										<Chip
											label={unreadCounts[room.id]}
											size="small"
											color="error"
											sx={{ height: 18, fontSize: "0.7rem", minWidth: 24 }}
										/>
									)}
								</Box>
							</TableCell>
							<TableCell>{getChatTypeChip(room.chatPurpose)}</TableCell>
							<TableCell>{room.memberCount || "-"}</TableCell>
							<TableCell>
								<Typography variant="body2" sx={{ color: "#666" }}>
									{room.description || "-"}
								</Typography>
							</TableCell>
						</TableRow>
					))}
					{rooms.length === 0 && !loading && (
						<TableRow>
							<TableCell colSpan={4} sx={{ textAlign: "center", color: "#999" }}>
								{emptyText}
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

export default function Club() {
	const [loading, setLoading] = useState(true);
	const [club, setClub] = useState<ClubType | null>();
	const [communityChats, setCommunityChats] = useState<MatrixRoomInfo[]>([]);
	const [privateChats, setPrivateChats] = useState<MatrixRoomInfo[]>([]);
	const [matrixLoading, setMatrixLoading] = useState(false);
	const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
	const [activeTab, setActiveTab] = useState(0);
	const { isSuperAdmin, clubId: selfClubId, hasClubLevelAccess } = useSelfUser();
	const isUsersVisible = isSuperAdmin;
	const isChatListVisible = hasClubLevelAccess;
	const { clubId: routeClubId } = useParams<{ clubId: string }>();
	const clubId = routeClubId || selfClubId;
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const openMenu = Boolean(anchorEl);
	const navigate = useNavigate();
	const { openConfirmModal: openFinalConfirmModal } = useConfirmModal({
		title: `Are you really sure you want to delete ${club?.name} club? All chats, users, assets etc will be deleted. This action is irreversible.`,
		onConfirm: () => {
			deleteClub();
		},
	});
	const { openConfirmModal, closeModal } = useConfirmModal({
		title: `Are you sure you want to delete ${club?.name} club?`,
		onConfirm: () => {
			closeModal();
			setTimeout(() => {
				openFinalConfirmModal({});
			}, 600);
		},
	});
	const openSnackbar = useSnackbar();

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const getClub = async () => {
		try {
			setLoading(true);
			const { data, status } = await API.get(`club/${clubId}`);
			if (status === 200 && !!data) {
				setClub(data);
				loadChats();
			} else {
				setClub(null);
			}
		} catch (error) {
			console.log(error);
			setClub(null);
		}
		setLoading(false);
	};

	const loadChats = async () => {
		if (!matrixClient.isLoggedIn()) return;
		if (!clubId) return;
		setMatrixLoading(true);
		// matrix/private_chats is super-admin-only: club roles must not request
		// it, and its failure must not wipe the community chat list.
		const { communityChats: community, privateChats: priv } = await loadClubPageChats(
			matrixClient,
			clubId,
			{ isSuperAdmin }
		);
		setCommunityChats(community);
		setPrivateChats(priv);
		try {
			const counts = await matrixClient.getUnreadCounts();
			setUnreadCounts(counts);
		} catch {
			/* non-critical */
		}
		setMatrixLoading(false);
	};

	const deleteClub = async () => {
		try {
			const { status } = await API.delete(`club/${club?.id}`);
			if (status === 200) {
				openSnackbar("Club deleted successfully");
				navigate("/clubs");
			}
		} catch (error) {
			openSnackbar("Error deleting club");
			console.error("Error deleting club:", error);
		}
	};

	useEffect(() => {
		getClub();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleChatClick = (chatId: string) => {
		navigate(`/club/${clubId}/chat/${encodeURIComponent(chatId)}`);
	};

	const isPrivateTabVisible = isSuperAdmin;
	const showPrivate = isPrivateTabVisible && activeTab === 1;
	const activeRooms = showPrivate ? privateChats : communityChats;
	const emptyText = showPrivate ? "No private chats" : "No community chats";

	return (
		<>
			<Paper
				sx={{
					p: 2,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography component="h2" variant="h6" color="primary" gutterBottom>
						{club?.name}
					</Typography>
					<div>
						<IconButton
							aria-label="more"
							id="long-button"
							aria-controls={openMenu ? "long-menu" : undefined}
							aria-expanded={openMenu ? "true" : undefined}
							aria-haspopup="true"
							onClick={handleClick}
						>
							<MoreVertIcon />
						</IconButton>
						<Menu
							id="long-menu"
							MenuListProps={{
								"aria-labelledby": "long-button",
							}}
							anchorEl={anchorEl}
							open={openMenu}
							onClose={handleClose}
						>
							<MenuItem onClick={() => navigate(`/club/${clubId}/edit`)}>Edit Club</MenuItem>
							{isSuperAdmin && <MenuItem onClick={openConfirmModal}>Delete Club</MenuItem>}
						</Menu>
					</div>
				</div>
				{loading && (
					<Box sx={{ width: "100%", my: 0.5 }}>
						<LinearProgress />
					</Box>
				)}
				{isChatListVisible && (
					<>
						{!matrixClient.isLoggedIn() && (
							<Typography variant="body2" sx={{ p: 2, color: "#999" }}>
								Matrix not connected. The chat will prompt for login when opened.
							</Typography>
						)}

						<Tabs
							value={activeTab}
							onChange={(_, v) => setActiveTab(v)}
							sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}
						>
							<Tab
								icon={<GroupIcon sx={{ fontSize: 16 }} />}
								iconPosition="start"
								label={`Community Chats${communityChats.length > 0 ? ` (${communityChats.length})` : ""}`}
								sx={{ textTransform: "none", minHeight: 40, fontSize: "0.85rem" }}
							/>
							{isPrivateTabVisible && (
								<Tab
									icon={<LockIcon sx={{ fontSize: 16 }} />}
									iconPosition="start"
									label={`Private Chats${privateChats.length > 0 ? ` (${privateChats.length})` : ""}`}
									sx={{ textTransform: "none", minHeight: 40, fontSize: "0.85rem" }}
								/>
							)}
						</Tabs>

						{matrixLoading && (
							<Box sx={{ width: "100%", my: 0.5 }}>
								<LinearProgress />
							</Box>
						)}

						<ChatTable
							rooms={activeRooms}
							loading={matrixLoading}
							onChatClick={handleChatClick}
							unreadCounts={unreadCounts}
							emptyText={emptyText}
						/>

						<div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "16px", gap: "8px" }}>
							<Button
								variant="outlined"
								color="primary"
								onClick={() => loadChats()}
								disabled={matrixLoading}
							>
								Refresh Rooms
							</Button>
							<Button
								variant="contained"
								color="primary"
								onClick={() => navigate(`/club/${clubId}/chat/create`)}
							>
								Create Chat
							</Button>
						</div>
					</>
				)}
			</Paper>
			{isUsersVisible && (
				<div style={{ marginTop: "32px" }}>
					<SearchUser clubId={clubId} />
				</div>
			)}
		</>
	);
}
